import createReducer from 'redux/createReducer';

import type { BasketBuilderExperienceQuickShopWidgetResponse } from 'api/definitions/basket-builder-experience/types';
import type { TransformedBasketBuilderExperienceSuggestionsResponse } from 'redux/transforms/transform-basket-builder-suggestions-into-legacy-format';
import type { LegacyProductReference } from 'constants/products';
import { REASON_NO_SUGGESTIONS, STATUS_INELIGIBLE } from 'constants/quick-shop';

import { GET_QUICK_SHOP_WIDGET_SUGGESTIONS } from 'redux/modules/quick-shop-widget/actions/types';

import type {
  QuickShopWidgetState,
  QuickShopWidgetRequestState,
  QuickShopWidgetSuccessState,
} from 'redux/modules/quick-shop-widget/types';

import { initialState } from 'redux/modules/quick-shop-widget/state';

export type BasketBuilderExperienceSuggestionsFetcherResult = Pick<
  TransformedBasketBuilderExperienceSuggestionsResponse<BasketBuilderExperienceQuickShopWidgetResponse>,
  'eligibility' | 'recommendedSelection'
> & {
  suggestions: LegacyProductReference[];
};

export type BasketBuilderExperienceSuggestionsSuccessAction = {
  result: BasketBuilderExperienceSuggestionsFetcherResult;
};

const isQuickShopWidgetResolved = (
  state: QuickShopWidgetState,
): state is QuickShopWidgetSuccessState => 'suggestions' in state;

const getQuickShopWidgetSuggestionsRequest = (
  state: QuickShopWidgetState,
): QuickShopWidgetRequestState | QuickShopWidgetSuccessState => {
  if (isQuickShopWidgetResolved(state)) {
    return {
      ...state,
      loading: true,
    };
  }

  return {
    loading: true,
  };
};

const getQuickShopWidgetSuggestionsSuccess = (
  _state: QuickShopWidgetState,
  {
    result: { eligibility, suggestions, recommendedSelection },
  }: BasketBuilderExperienceSuggestionsSuccessAction,
): QuickShopWidgetSuccessState => {
  return {
    loading: false,
    eligibility,
    recommendedSelection,
    suggestions,
  };
};

const getQuickShopWidgetSuggestionsFailure = (): QuickShopWidgetSuccessState => {
  return {
    loading: false,
    eligibility: {
      status: STATUS_INELIGIBLE,
      reason: REASON_NO_SUGGESTIONS,
    },
    recommendedSelection: [],
    suggestions: [],
  };
};

const actionTypeReducerMap = [
  [GET_QUICK_SHOP_WIDGET_SUGGESTIONS.request, getQuickShopWidgetSuggestionsRequest],
  [GET_QUICK_SHOP_WIDGET_SUGGESTIONS.success, getQuickShopWidgetSuggestionsSuccess],
  [GET_QUICK_SHOP_WIDGET_SUGGESTIONS.failure, getQuickShopWidgetSuggestionsFailure],
] as const;

export type PayloadMap = ReducerMapTupleToRecord<typeof actionTypeReducerMap>;
export default createReducer(initialState, actionTypeReducerMap);
